import type { ItemType, LibrarySort, LibrarySortDir, SharingFilter } from '@/pages/library/components/LibraryFilters'

/**
 * The Library's search, type, sharing and sort, as one value that lives in the
 * URL query. The page reads it from the URL and `show_library` navigates to it,
 * so a bookmark, Back, and the assistant all set the same state.
 */
export interface LibraryView {
  search: string
  type: ItemType
  sharing: SharingFilter
  sort: LibrarySort
  dir: LibrarySortDir
}

export const DEFAULT_LIBRARY_VIEW: LibraryView = {
  search: '',
  type: 'all',
  sharing: 'all',
  sort: 'dateUpdated',
  dir: 'desc',
}

/** The server's cap on the Library search (`chatRequest.ts` `filters.search`), shared by the URL and the context. */
export const MAX_SEARCH = 200

/** Who can see an item; the `sharing` filter adds `all` in front. */
export const LIBRARY_SHARING_LEVELS = ['owner', 'organization', 'groups', 'public'] as const
export type LibrarySharingLevel = (typeof LIBRARY_SHARING_LEVELS)[number]

export const isLibrarySharingLevel = (v: unknown): v is LibrarySharingLevel =>
  (LIBRARY_SHARING_LEVELS as readonly unknown[]).includes(v)

const OPTIONS = {
  type: ['all', 'layer', 'map', 'dashboard', 'file', 'csv_table'],
  sharing: ['all', ...LIBRARY_SHARING_LEVELS],
  sort: ['dateCreated', 'dateUpdated', 'name', 'type', 'size', 'visibility'],
  dir: ['asc', 'desc'],
} as const satisfies Record<Exclude<keyof LibraryView, 'search'>, readonly string[]>

type EnumKey = keyof typeof OPTIONS

const ENUM_KEYS = Object.keys(OPTIONS) as EnumKey[]

const isOption = <K extends EnumKey>(key: K, v: unknown): v is LibraryView[K] =>
  (OPTIONS[key] as readonly unknown[]).includes(v)

export function parseLibraryView(params: URLSearchParams): LibraryView {
  const view: LibraryView = { ...DEFAULT_LIBRARY_VIEW, search: (params.get('q') ?? '').slice(0, MAX_SEARCH) }

  for (const key of ENUM_KEYS) {
    const v = params.get(key)
    if (isOption(key, v)) Object.assign(view, { [key]: v })
  }

  return view
}

export function libraryViewToSearch(view: LibraryView): string {
  const params = new URLSearchParams()

  if (view.search) params.set('q', view.search)
  for (const key of ENUM_KEYS) {
    if (view[key] !== DEFAULT_LIBRARY_VIEW[key]) params.set(key, view[key])
  }

  const s = params.toString()

  return s ? `?${s}` : ''
}

/** A model-authored view: every key optional, every bad value named. */
export function readLibraryView(input: unknown): LibraryView | { error: string } {
  const raw = (input ?? {}) as Record<string, unknown>
  const view: LibraryView = { ...DEFAULT_LIBRARY_VIEW }

  if (raw.search !== undefined) {
    if (typeof raw.search !== 'string') return { error: 'search must be a string.' }
    view.search = raw.search.trim().slice(0, MAX_SEARCH)
  }

  for (const key of ENUM_KEYS) {
    if (raw[key] === undefined) continue
    if (!isOption(key, raw[key])) return { error: `${key} must be one of: ${OPTIONS[key].join(', ')}.` }
    Object.assign(view, { [key]: raw[key] })
  }

  return view
}

export const LIBRARY_ITEM_TYPES = ['layer', 'map', 'dashboard', 'file', 'csv_table'] as const
export type LibraryItemKind = (typeof LIBRARY_ITEM_TYPES)[number]

export const isLibraryItemType = (v: unknown): v is LibraryItemKind => (LIBRARY_ITEM_TYPES as readonly unknown[]).includes(v)
